import { useTranslation } from 'react-i18next';

import { Typography } from '@cfa/react-components';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { itemByLanguage } from '@/utils/language';
import { getClickableLink } from '@/utils/url';

import { trackPlanTaskBounce } from './trackPlanTaskBounce';

import { PlanUrlSet } from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

const nativeUpdatedLinkFunctionalityEnabled =
  (window as { nativeUpdatedLinkFeatureFlag?: boolean })
    .nativeUpdatedLinkFeatureFlag ?? false;

interface PlanItemStepLinkProps {
  link: PlanUrlSet;
  language?: string;
  planId: string;
  stepId?: string;
  stepName?: string;
}

const PlanItemStepLink: React.FC<PlanItemStepLinkProps> = ({
  link,
  language,
  planId,
  stepId,
  stepName,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();

  const content = (
    <>
      <Typography variant="body1">{t('Generic.link')}</Typography>
      <Typography
        className="text-secondary-base hover:text-secondary-hover wrap-break-word underline hover:cursor-pointer hover:font-normal hover:underline"
        variant="body1"
      >
        {itemByLanguage(link.urlSet, undefined, language)}
      </Typography>
    </>
  );

  const url = getClickableLink(itemByLanguage(link.urlSet, '', language));

  const trackLinkClick = () => {
    trackPlanTaskBounce(track, {
      url,
      planId,
      stepId,
      stepName,
    });
  };

  if (window.native.isNative && nativeUpdatedLinkFunctionalityEnabled) {
    return (
      <button
        type="button"
        className="pb-2.5! text-left"
        onClick={() => {
          trackLinkClick();
          sendNativeMessage({
            type: 'planLinkClicked',
            payload: url,
          });
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      className="text-secondary-base! hover:text-secondary-hover! hover:[&>p]:text-secondary-hover! [&>p]:text-secondary-base! pb-2.5"
      href={url}
      onClick={trackLinkClick}
      target="_blank"
      rel="noreferrer"
    >
      {content}
    </a>
  );
};

export default PlanItemStepLink;
